import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Star, Loader2 } from "lucide-react";
import { sharedApi } from "@/lib/api";
import { useAuth } from "@/lib/auth";

/**
 * ReviewCard (Iter-237) — 5-star + text review form and public list.
 * • Anonymous users see stars + list; submitting prompts sign-in.
 * • Signed-in users submit one review per ad; resubmit updates it.
 * • Sellers CANNOT review their own ad (backend returns 400).
 */

function Stars({ value, size = "sm", onSelect, testIdPrefix }) {
  const [hover, setHover] = useState(0);
  const active = hover || value || 0;
  const iconDim = size === "lg" ? "w-7 h-7" : size === "md" ? "w-5 h-5" : "w-4 h-4";
  // Interactive buttons need a proper tap target (≥44px per iter-240 fix
  // for the review-stars-don't-register bug). Static display stars stay
  // compact.
  const btnDim = size === "lg" ? "w-11 h-11" : "w-9 h-9";
  return (
    <div
      className={`inline-flex items-center ${onSelect ? "gap-1" : "gap-0.5"}`}
      role={onSelect ? "radiogroup" : "img"}
      aria-label={onSelect ? "Choose a rating" : `${value ?? 0} out of 5`}
      onMouseLeave={() => onSelect && setHover(0)}
    >
      {[1, 2, 3, 4, 5].map((i) => {
        const filled = i <= active;
        if (onSelect) {
          return (
            <button
              key={i}
              type="button"
              role="radio"
              aria-checked={value === i}
              onClick={(e) => {
                // Guard against the click bubbling to any outer form's
                // submit handler (React re-renders sometimes swallow the
                // click when the button dimension equals the icon).
                e.preventDefault();
                e.stopPropagation();
                onSelect(i);
              }}
              onMouseEnter={() => setHover(i)}
              data-testid={testIdPrefix ? `${testIdPrefix}-${i}` : undefined}
              className={`${btnDim} grid place-items-center rounded-full transition-colors hover:bg-amber-50 ${
                filled ? "text-amber-400" : "text-slate-300"
              }`}
              aria-label={`${i} star${i === 1 ? "" : "s"}`}
            >
              <Star className={iconDim} fill={filled ? "currentColor" : "none"} />
            </button>
          );
        }
        return (
          <Star
            key={i}
            className={`${iconDim} ${filled ? "text-amber-400" : "text-slate-300"}`}
            fill={filled ? "currentColor" : "none"}
          />
        );
      })}
    </div>
  );
}

export function StarSummary({ avg, count }) {
  if (!count) return null;
  const pretty = Number.isFinite(avg) ? Number(avg).toFixed(1) : "0.0";
  return (
    <div
      data-testid="detail-review-summary"
      className="inline-flex items-center gap-1.5 rounded-full bg-amber-50 border border-amber-200 px-2.5 py-1 text-xs font-semibold text-amber-800"
    >
      <Stars value={Math.round(avg || 0)} size="sm" />
      <span data-testid="detail-review-avg">{pretty}</span>
      <span className="text-amber-700/70">·</span>
      <span data-testid="detail-review-count">
        {count} review{count === 1 ? "" : "s"}
      </span>
    </div>
  );
}

export default function ReviewCard({ listingId, ownerId, avg, count }) {
  const auth = useAuth();
  const qc = useQueryClient();
  const isOwner = !!(auth?.user?.id && ownerId && auth.user.id === ownerId);

  const listQ = useQuery({
    queryKey: ["listing-reviews", listingId],
    queryFn: async () => {
      const { data } = await sharedApi.get(
        `/classifieds/listings/${listingId}/reviews`
      );
      return data;
    },
    enabled: !!listingId,
  });

  const items = listQ.data?.reviews || [];
  const summary = useMemo(
    () => ({
      avg: listQ.data?.review_avg ?? avg ?? 0,
      count: listQ.data?.review_count ?? count ?? 0,
    }),
    [listQ.data?.review_avg, listQ.data?.review_count, avg, count]
  );

  const mine = auth?.user
    ? items.find((r) => r.reviewer_id === auth.user.id || r.is_mine)
    : null;

  const [rating, setRating] = useState(mine?.rating || 0);
  const [text, setText] = useState(mine?.text || "");

  // Keep form seeded with the user's existing review after list loads.
  React.useEffect(() => {
    if (mine) {
      setRating(mine.rating || 0);
      setText(mine.text || "");
    }
  }, [mine?.rating, mine?.text]);

  const submitMut = useMutation({
    mutationFn: async () => {
      const { data } = await sharedApi.post(
        `/classifieds/listings/${listingId}/reviews`,
        { rating, text: text.trim() || undefined }
      );
      return data;
    },
    onSuccess: (data) => {
      toast.success(mine ? "Review updated." : "Thanks for your review!");
      qc.invalidateQueries({ queryKey: ["listing-reviews", listingId] });
      qc.invalidateQueries({ queryKey: ["listing", listingId] });
      if (data?.review_avg != null && data?.review_count != null) {
        listQ.refetch();
      }
    },
    onError: (err) => {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Couldn't submit review.");
    },
  });

  const disabled = !rating || submitMut.isPending || isOwner;

  return (
    <section
      data-testid="review-card"
      className="rounded-3xl bg-white shadow-card p-6"
    >
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <div className="text-xs font-bold uppercase tracking-[0.2em] text-slate-400">
            Reviews
          </div>
          <div className="text-slate-800 font-display text-lg font-semibold">
            Add Review
          </div>
        </div>
        {summary.count > 0 && <StarSummary avg={summary.avg} count={summary.count} />}
      </div>

      {isOwner ? (
        <div
          data-testid="review-owner-block"
          className="mt-4 rounded-xl bg-slate-50 border border-soft px-3 py-3 text-xs text-slate-600"
        >
          You can&apos;t review your own listing.
        </div>
      ) : !auth?.user ? (
        <div
          data-testid="review-signin-cta"
          className="mt-4 rounded-xl bg-[color:var(--brand-blue-wash)]/50 border border-[color:var(--brand-blue-wash)] px-3 py-3 flex items-center justify-between gap-3"
        >
          <div className="text-xs text-slate-600">
            Sign in to leave a review.
          </div>
          <Link
            to="/login"
            data-testid="review-signin-link"
            className="rounded-full bg-[color:var(--brand-blue)] hover:brightness-110 text-white font-semibold px-4 py-1.5 text-xs"
          >
            Sign in
          </Link>
        </div>
      ) : (
        <form
          className="mt-4 space-y-3"
          onSubmit={(e) => {
            e.preventDefault();
            if (!disabled) submitMut.mutate();
          }}
        >
          <div>
            <div className="text-xs font-semibold text-slate-600 mb-1">
              Your rating
            </div>
            <Stars
              value={rating}
              size="lg"
              onSelect={setRating}
              testIdPrefix="review-star"
            />
          </div>
          <label className="block">
            <span className="text-xs font-semibold text-slate-600">
              Your review (optional)
            </span>
            <textarea
              data-testid="review-text"
              value={text}
              onChange={(e) => setText(e.target.value)}
              className="input mt-1 min-h-[96px]"
              placeholder="Tell buyers what stood out."
            />
            <span className="block mt-1 text-[11px] text-slate-400" data-testid="review-bonus-hint">
              Bonus points need at least 20 words or a photo — up to 3 bonuses a day, 10 a month.
            </span>
          </label>
          <div className="flex items-center justify-end">
            <button
              type="submit"
              disabled={disabled}
              data-testid="review-submit"
              className="rounded-full bg-[color:var(--brand-blue)] hover:brightness-110 disabled:opacity-50 disabled:cursor-not-allowed text-white font-semibold px-6 py-2 text-sm inline-flex items-center gap-2"
            >
              {submitMut.isPending ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  {mine ? "Updating…" : "Submitting…"}
                </>
              ) : mine ? (
                "Update review"
              ) : (
                "Submit Review"
              )}
            </button>
          </div>
        </form>
      )}

      {items.length > 0 && (
        <ul
          className="mt-5 pt-4 border-t border-subtle space-y-4"
          data-testid="review-list"
        >
          {items.slice(0, 8).map((r, i) => (
            <li
              key={r.id || i}
              className="flex items-start gap-3"
              data-testid={`review-item-${i}`}
            >
              <div className="w-9 h-9 shrink-0 rounded-full bg-[color:var(--brand-blue-wash)] text-[color:var(--brand-blue)] grid place-items-center text-sm font-bold">
                {(r.reviewer_name || "A").slice(0, 1).toUpperCase()}
              </div>
              <div className="min-w-0 flex-1">
                <div className="flex items-center flex-wrap gap-2">
                  <span className="text-sm font-semibold text-slate-800">
                    {r.reviewer_name || "Anonymous"}
                  </span>
                  <Stars value={r.rating} size="sm" />
                  {r.created_at && (
                    <span className="text-[11px] text-slate-400">
                      {new Date(r.created_at).toLocaleDateString("en-NZ", {
                        day: "2-digit",
                        month: "short",
                        year: "numeric",
                      })}
                    </span>
                  )}
                </div>
                {r.text && (
                  <p className="text-sm text-slate-600 mt-1 whitespace-pre-wrap">
                    {r.text}
                  </p>
                )}
              </div>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
